import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { fmtTime12 } from '../../utils/time';

const fmtDT = v => { try { return format(new Date(v), 'd MMM yy h:mm a'); } catch { return v || '–'; } };
const fmtDate = v => { try { return format(new Date(v), 'd MMM yy h:mm a'); } catch { return v || '–'; } };

const COLS = [
  { key: 'shift_date',     label: 'Shift Date' },
  { key: 'staff_name',     label: 'Staff Name' },
  { key: 'client_names',   label: 'Client(s)' },
  { key: 'shift_type',     label: 'Shift Type' },
  { key: 'rostered_start', label: 'Rostered Start', render: fmtTime12 },
  { key: 'rostered_end',   label: 'Rostered End',   render: fmtTime12 },
  { key: 'rostered_hours', label: 'Rostered Hrs', render: v => v ? `${parseFloat(v).toFixed(2)}h` : '–' },
  { key: 'clock_in_time',  label: 'Clock In',     render: fmtDT },
  { key: 'clock_out_time', label: 'Clock Out',    render: fmtDT },
  { key: 'hours_worked',   label: 'Actual Hrs',   render: v => v ? `${parseFloat(v).toFixed(2)}h` : '–' },
  { key: 'break_minutes',  label: 'Break (min)',  render: v => v || '0' },
  { key: 'approved_at',    label: 'Approved At',  render: fmtDate },
];

export default function TimesheetApproved() {
  const [data, setData]         = useState([]);
  const [staff, setStaff]       = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [staffId, setStaffId]   = useState('');
  const [clientId, setClientId] = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffId)  p.set('staff_id', staffId);
    if (clientId) p.set('client_id', clientId);
    api.get(`/reports/timesheet-approved?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, staffId, clientId]);

  useEffect(() => { load(); }, [load]);

  const totalHours = data.reduce((s, r) => s + parseFloat(r.hours_worked || 0), 0);
  const selStaff   = staff.find(s => s.id === staffId);
  const selClient  = clients.find(c => c.id === clientId);
  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selStaff  ? [{ label: 'Staff',  value: `${selStaff.first_name} ${selStaff.last_name}`,   onRemove: () => setStaffId('') }]  : []),
    ...(selClient ? [{ label: 'Client', value: `${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
  ];

  return (
    <ReportLayout title="Timesheet Report (Approved)" data={data} filename="timesheet_approved"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select></div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }>
      {data.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 mb-1">Approved Timesheets</div>
            <div className="text-2xl font-bold text-slate-900">{data.length}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 mb-1">Total Approved Hours</div>
            <div className="text-2xl font-bold text-slate-900">{totalHours.toFixed(2)}h</div>
          </div>
        </div>
      )}
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination} />
    </ReportLayout>
  );
}
